import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { UsageMeter } from '../../../../ui/usage_meter/UsageMeter';
import { CreditUsageResponse, creditTone, formatResetDate } from '../../../../utils/types/credits_types';

interface ICreditUsagePanelProps {
  usage: CreditUsageResponse | null;
}

export const CreditUsagePanel: React.FC<ICreditUsagePanelProps> = ({ usage }) => {
  if (!usage) return null;

  const usedPercent = usage.unlimited
    ? 0
    : Math.min(100, Math.round((usage.current.used / Math.max(usage.monthlyLimit ?? 1, 1)) * 100));
  const tone = creditTone(usage.current);
  const chipColor: 'error' | 'warning' | 'success' =
    tone === 'crit' ? 'error' : tone === 'warn' ? 'warning' : 'success';

  const peak = Math.max(usage.monthlyLimit ?? 0, ...usage.history.map((entry) => entry.used), 1);

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}
      data-testid="credit-usage-panel"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
            Credit usage · CandiBank {usage.unit}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mt: 0.25 }}>
            {usage.unlimited
              ? 'No monthly limit set — uploads are unlimited and still counted.'
              : `${usage.monthlyLimit?.toLocaleString()} ${usage.unit} per month · resets ${formatResetDate(usage.current)}`}
          </Typography>
        </Box>
        <Chip
          label={usage.unlimited ? 'Unlimited' : `${usedPercent}% used`}
          size="small"
          color={chipColor}
          sx={{
            fontWeight: 800,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderRadius: 2,
          }}
        />
      </Box>

      <UsageMeter
        used={usage.current.used}
        limit={usage.current.monthlyLimit}
        tone={tone}
        leftLabel={`This month: ${usage.current.used.toLocaleString()} used`}
        rightLabel={usage.unlimited ? '' : `${(usage.current.remaining ?? 0).toLocaleString()} left`}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          mt: 4,
          gridTemplateColumns: `repeat(${usage.history.length}, minmax(0, 1fr))`,
        }}
      >
        {usage.history.map((entry) => (
          <Box key={entry.period} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '100%', height: 64, display: 'flex', alignItems: 'flex-end' }}>
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '4px 4px 0 0',
                  bgcolor: entry.period === usage.current.period ? 'primary.main' : 'primary.light',
                  height: `${Math.max(4, Math.round((entry.used / peak) * 100))}%`,
                }}
                title={`${entry.period}: ${entry.used}`}
              />
            </Box>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary', fontSize: '0.625rem' }}>
              {entry.period.slice(2)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
              {entry.used.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
